import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Wraps the slot the plan broadcasts into (plans/_changed). Each notice
// arrives hidden carrying the plan's version and who changed it. A newer
// version from someone else means the page is stale; the viewer's own saves
// are ignored, since the paths that update the page in place never reload it
// and would otherwise announce the viewer's edit back to them.
//
// With the live_plan_refresh flag on (liveValue), a stale page refreshes
// itself with a Turbo page refresh (morph, scroll preserved; after_morph.js
// puts the UI state back), so other people's edits show up live. The one
// exception is a viewer mid-edit outside a modal: a morph would reset what
// they are typing, so they get the notice and a button instead. Open modals
// are data-turbo-permanent and ride a morph out (see
// setup/modal_morph_persistence.js), so edits in them don't hold it up. With
// the flag off every stale page gets the notice; its button does the same
// in-place refresh.
//
// `latest` is the newest foreign version seen. The morph brings the server's
// version in through versionValue, so comparing the two after it tells
// whether a change landed while the refresh was in flight (Turbo drops a
// refresh during a visit) and needs one more.
export default class extends Controller {
  static targets = ["banner"]
  static values = { version: Number, user: Number, live: Boolean }

  versionValueChanged() {
    this.sync()
  }

  bannerTargetConnected(banner) {
    const version = Number(banner.dataset.version)
    const foreign = Number(banner.dataset.actor) !== this.userValue
    if (foreign && version > (this.latest || 0)) this.latest = version
    this.sync()
  }

  sync() {
    const stale = this.latest > this.versionValue
    if (!this.hasBannerTarget) return

    if (stale && this.liveValue && !this.editing) return this.refresh()

    this.bannerTarget.hidden = !stale
  }

  refresh() {
    this.bannerTarget.setAttribute("aria-busy", "true")
    Turbo.session.refresh(window.location.href)
  }

  // A dirty form or a field being typed in, outside any open modal.
  get editing() {
    const focused = document.activeElement
    const typing = focused?.matches("input:not([type=checkbox]):not([type=radio]), textarea, [contenteditable]")

    return [document.querySelector("form[data-dirty]"), typing && focused]
      .some(element => element && !element.closest(".modal.show"))
  }
}
